import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { usePageHeaderApi } from "../context/PageHeaderContext";
import { Icon24 } from "./ui";
import "./PageHeader.scss";

export default function PageHeader() {
  const { header } = usePageHeaderApi();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  const editableTitle = header?.editableTitle;

  // 편집 모드 시작
  const handleEdit = () => {
    if (editableTitle) {
      setEditValue(editableTitle.value || "");
      setIsEditing(true);
    }
  };

  // 편집 모드 종료 (저장)
  const handleSave = async () => {
    if (!editableTitle || !editValue.trim()) return;
    
    setIsSaving(true);
    try {
      await editableTitle.onSave(editValue.trim());
      setIsEditing(false);
    } catch (error) {
      console.error("저장 실패:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // 편집 모드 취소
  const handleCancel = () => {
    setIsEditing(false);
    setEditValue("");
    if (editableTitle?.onCancel) {
      editableTitle.onCancel();
    }
  };

  // 편집 모드일 때 input에 포커스
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // editableTitle 값이 변경되면 editValue도 업데이트
  useEffect(() => {
    if (editableTitle && !isEditing) {
      setEditValue(editableTitle.value || "");
    }
  }, [editableTitle?.value, isEditing]);

  // 헤더가 없을 때도 공간을 확보하기 위해 빈 div 반환
  if (!header) {
    return <div className="page-header page-header--empty" aria-hidden="true" />;
  }

  const title = header.title || "";
  const subtitle = header.subtitle || "";
  const subtitleIsPlainText = typeof subtitle === "string";
  const showBack = Boolean(header.showBack) && typeof header.onBack === "function";
  const actions = header.actions;

  const hasAnything = Boolean(title || subtitle || showBack || actions || editableTitle);
  // 헤더에 표시할 내용이 없어도 공간은 확보
  if (!hasAnything) {
    return <div className="page-header page-header--empty" aria-hidden="true" />;
  }

  const isCentered = Boolean(title) && !subtitle && !showBack && !actions && !editableTitle;

  return (
    <div className={`page-header ${isCentered ? "page-header--center" : ""}`}>
      <div className="page-header-inner">
        <div className="page-header-left">
          {showBack ? (
            <button className="page-header-back" type="button" onClick={header.onBack}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          ) : null}
          <div className="page-header-titles">
            {title ? (
              <div className="page-header-title">
                {editableTitle && isEditing ? (
                  <>
                    <input
                      ref={inputRef}
                      type="text"
                      className="page-header-title-input"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSave();
                        if (e.key === "Escape") handleCancel();
                      }}
                      placeholder={editableTitle.placeholder || ""}
                      disabled={isSaving}
                    />
                    <div className="page-header-title-edit-actions">
                      <button
                        className="page-header-title-save-btn"
                        onClick={handleSave}
                        disabled={isSaving || !editValue.trim()}
                        type="button"
                      >
                        저장
                      </button>
                      <button
                        className="page-header-title-cancel-btn"
                        onClick={handleCancel}
                        disabled={isSaving}
                        type="button"
                      >
                        취소
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="page-header-title-text">{title}</span>
                    {editableTitle ? (
                      <button
                        className="page-header-title-edit-btn"
                        onClick={handleEdit}
                        title="수정"
                        type="button"
                      >
                        <Icon24 name="checkCircleFill" />
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}

            {/* subtitle은 늦게 로딩되는 경우가 많아(예: projectName) 헤더 높이가 바뀌면서 레이아웃이 흔들릴 수 있음
                → subtitle이 비어 있어도 한 줄 공간을 예약해 "위/아래로 밀리는" 현상을 방지 */}
            {subtitle ? (
              subtitleIsPlainText ? (
                <div className="page-header-subtitle">{subtitle}</div>
              ) : (
                <div className="page-header-subtitle-custom">{subtitle}</div>
              )
            ) : (
              <div className="page-header-subtitle page-header-subtitle--placeholder" aria-hidden="true">
                &nbsp;
              </div>
            )}
          </div>
        </div>
        {actions ? <div className="page-header-actions">{actions}</div> : null}
      </div>
    </div>
  );
}

